/**
 * AddTaskDrawer — slide-in panel for creating a founder task.
 *
 * Flow:
 *  1. User fills form (title, description, agent, priority)
 *  2. User adds files via FileUploadZone (pending, not yet uploaded)
 *  3. "Save Task" → POST /api/workspaces/:id/tasks → get task id
 *  4. For each pending file:
 *       POST /api/uploads/signed-upload-url → { upload_url, gcs_path }
 *       PUT upload_url (directly to GCS)
 *       POST /api/uploads/confirm → saves file_attachments record
 *  5. onSaved(task) called → drawer closes
 *
 * Guard: if form is dirty, clicking backdrop or pressing Escape shows a
 * confirm dialog instead of closing immediately.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import FileUploadZone from './FileUploadZone';
import { apiFetch } from '../services/api';

const AGENTS = ['ceo','builder','content','marketing','research','ops','founder'];
const AGENT_COLORS = {
  ceo:'#0e0e0d', builder:'#378add', content:'#5dcaa5',
  marketing:'#c8563a', research:'#e8a87c', ops:'#c8563a', founder:'#0e0e0d',
};

const EMPTY_FORM = { title: '', description: '', agent_type: 'ceo', priority: 3 };

function isDirty(form, files) {
  return (
    form.title.trim() !== '' ||
    form.description.trim() !== '' ||
    form.agent_type !== 'ceo' ||
    form.priority !== 3 ||
    files.length > 0
  );
}

// ─── Upload a single file ──────────────────────────────────────────────────

async function uploadFile({ workspaceId, taskId, file }) {
  // 1. Get signed PUT URL
  const { upload_url, gcs_path } = await apiFetch('/api/uploads/signed-upload-url', {
    method: 'POST',
    body: JSON.stringify({
      workspace_id: workspaceId,
      entity_type:  'task',
      entity_id:    taskId,
      filename:     file.name,
      file_size:    file.size,
    }),
  });

  // 2. PUT directly to GCS
  const putResp = await fetch(upload_url, {
    method:  'PUT',
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
    body:    file,
  });
  if (!putResp.ok) throw new Error(`GCS upload failed: ${putResp.status}`);

  // 3. Confirm record
  await apiFetch('/api/uploads/confirm', {
    method: 'POST',
    body: JSON.stringify({
      workspace_id: workspaceId,
      entity_type:  'task',
      entity_id:    taskId,
      filename:     file.name,
      gcs_path,
      mime_type:    file.type || null,
      file_size:    file.size,
      uploaded_by:  'founder',
    }),
  });

  return gcs_path;
}


// ─── Drawer ───────────────────────────────────────────────────────────────

export default function AddTaskDrawer({ open, workspaceId, onClose, onSaved }) {
  const [form, setForm]         = useState(EMPTY_FORM);
  const [files, setFiles]       = useState([]);   // { id, filename, mime_type, file_size, status, file }
  const [saving, setSaving]     = useState(false);
  const [saveError, setSaveError] = useState('');
  const [confirmClose, setConfirmClose] = useState(false);
  const titleRef = useRef(null);

  // Focus title when drawer opens
  useEffect(() => {
    if (open) {
      setTimeout(() => titleRef.current?.focus(), 120);
    }
  }, [open]);

  // Escape key
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === 'Escape') tryClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, form, files]); // eslint-disable-line react-hooks/exhaustive-deps

  const tryClose = useCallback(() => {
    if (saving) return;
    if (isDirty(form, files)) {
      setConfirmClose(true);
    } else {
      doClose();
    }
  }, [saving, form, files]); // eslint-disable-line react-hooks/exhaustive-deps

  const doClose = () => {
    setForm(EMPTY_FORM);
    setFiles([]);
    setSaveError('');
    setConfirmClose(false);
    onClose();
  };

  // ── File handlers ────────────────────────────────────────────────────────

  const handleAddFiles = (rawFiles) => {
    const newEntries = rawFiles.map(f => ({
      id:        crypto.randomUUID(),
      filename:  f.name,
      mime_type: f.type || null,
      file_size: f.size,
      status:    'pending',   // pending | uploading | done | error
      file:      f,           // raw File object
    }));
    setFiles(prev => [...prev, ...newEntries]);
  };

  const handleRemoveFile = (id) => {
    setFiles(prev => prev.filter(f => f.id !== id));
  };

  // ── Save ─────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    setSaveError('');
    if (!form.title.trim()) {
      setSaveError('Title is required.');
      return;
    }

    setSaving(true);

    // 1. Create task
    let task;
    try {
      task = await apiFetch(`/api/workspaces/${workspaceId}/tasks`, {
        method: 'POST',
        body: JSON.stringify({
          title:       form.title.trim(),
          description: form.description.trim() || null,
          agent_type:  form.agent_type,
          priority:    form.priority,
        }),
      });
    } catch (err) {
      setSaveError(`Failed to create task: ${err.message}`);
      setSaving(false);
      return;
    }

    // 2. Upload pending files (mark each as uploading / done / error)
    const pendingFiles = files.filter(f => f.status === 'pending');
    let uploadFailed = false;

    for (const entry of pendingFiles) {
      // Mark uploading
      setFiles(prev => prev.map(f =>
        f.id === entry.id ? { ...f, status: 'uploading' } : f
      ));

      try {
        await uploadFile({
          workspaceId,
          taskId: task.id,
          file:   entry.file,
        });
        setFiles(prev => prev.map(f =>
          f.id === entry.id ? { ...f, status: 'done' } : f
        ));
      } catch (err) {
        uploadFailed = true;
        setFiles(prev => prev.map(f =>
          f.id === entry.id ? { ...f, status: 'error' } : f
        ));
      }
    }

    setSaving(false);

    if (uploadFailed) {
      setSaveError('Task saved, but some files failed to upload. You can retry or remove them.');
      // Don't close — let user see errors and decide
      return;
    }

    // All good
    onSaved(task);
    doClose();
  };

  if (!open) return null;

  return (
    <>
      {/* ── Backdrop ─────────────────────────────────────────────────────── */}
      <div
        onClick={tryClose}
        style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.3)',
          zIndex: 200,
          backdropFilter: 'blur(2px)',
        }}
      />

      {/* ── Drawer panel ─────────────────────────────────────────────────── */}
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0,
        width: 'min(520px, 100vw)',
        background: 'var(--paper)',
        borderLeft: '1px solid var(--paper-3)',
        boxShadow: '-4px 0 24px rgba(0,0,0,0.10)',
        zIndex: 201,
        display: 'flex', flexDirection: 'column',
        animation: 'slide-in-right 0.22s ease',
      }}>

        {/* Header */}
        <div style={{
          padding: '20px 24px 16px',
          borderBottom: '1px solid var(--paper-3)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div>
            <h2 style={{ fontFamily: 'var(--serif)', fontSize: '20px', fontWeight: '400', margin: 0 }}>
              New Task
            </h2>
            <p style={{ fontSize: '12px', color: 'var(--ink-mute)', margin: '2px 0 0' }}>
              Created by founder · pending
            </p>
          </div>
          <button
            onClick={tryClose}
            disabled={saving}
            style={{
              background: 'none', border: 'none', cursor: saving ? 'not-allowed' : 'pointer',
              fontSize: '18px', color: 'var(--ink-mute)', padding: '4px 8px',
              borderRadius: '6px', lineHeight: 1,
            }}
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>

          {/* Title */}
          <Field label="Title" required>
            <input
              ref={titleRef}
              value={form.title}
              onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
              placeholder="What needs to be done?"
              disabled={saving}
              style={inputStyle}
            />
          </Field>

          {/* Description */}
          <Field label="Description">
            <textarea
              value={form.description}
              onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
              placeholder="Context, goals, or instructions for the agent…"
              disabled={saving}
              rows={5}
              style={{ ...inputStyle, resize: 'vertical', minHeight: '100px' }}
            />
          </Field>

          {/* Agent */}
          <Field label="Assign to agent">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {AGENTS.map(a => {
                const active = form.agent_type === a;
                const color  = AGENT_COLORS[a];
                return (
                  <button
                    key={a}
                    onClick={() => setForm(p => ({ ...p, agent_type: a }))}
                    disabled={saving}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: '6px',
                      padding: '5px 12px', borderRadius: '100px', fontSize: '13px',
                      fontFamily: 'var(--sans)', cursor: 'pointer',
                      border: `1.5px solid ${active ? color : 'var(--paper-3)'}`,
                      background: active ? `${color}14` : 'var(--white)',
                      color: active ? color : 'var(--ink)',
                      fontWeight: active ? '600' : '400',
                      transition: 'all 0.12s',
                    }}
                  >
                    <span style={{
                      width: '6px', height: '6px', borderRadius: '50%',
                      background: color, flexShrink: 0,
                    }} />
                    {a.charAt(0).toUpperCase() + a.slice(1)}
                  </button>
                );
              })}
            </div>
          </Field>

          {/* Priority */}
          <Field label={`Priority  —  P${form.priority}`}>
            <input
              type="range" min={1} max={5} step={1}
              value={form.priority}
              disabled={saving}
              onChange={e => setForm(p => ({ ...p, priority: Number(e.target.value) }))}
              style={{ width: '100%', accentColor: 'var(--ink)' }}
            />
            <div style={{
              display: 'flex', justifyContent: 'space-between',
              fontSize: '11px', color: 'var(--ink-mute)', marginTop: '2px',
            }}>
              <span>P1 — Critical</span>
              <span>P3 — Normal</span>
              <span>P5 — Low</span>
            </div>
          </Field>

          {/* Files */}
          <Field label="Attachments">
            <FileUploadZone
              files={files}
              onAdd={handleAddFiles}
              onRemove={handleRemoveFile}
              disabled={saving}
            />
          </Field>

          {/* Error banner */}
          {saveError && (
            <div style={{
              marginTop: '12px', padding: '10px 14px', borderRadius: 'var(--r)',
              background: 'rgba(224,112,112,0.08)',
              border: '1px solid rgba(224,112,112,0.25)',
              fontSize: '13px', color: '#c03030',
            }}>
              {saveError}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid var(--paper-3)',
          display: 'flex', gap: '10px', justifyContent: 'flex-end',
          background: 'var(--white)',
        }}>
          <button onClick={tryClose} disabled={saving} style={cancelBtnStyle}>
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving} style={saveBtnStyle(saving)}>
            {saving ? 'Saving…' : 'Save Task'}
          </button>
        </div>
      </div>

      {/* ── Confirm-close dialog ──────────────────────────────────────────── */}
      {confirmClose && (
        <>
          <div style={{
            position: 'fixed', inset: 0, zIndex: 300,
            background: 'rgba(0,0,0,0.4)',
          }} />
          <div style={{
            position: 'fixed', top: '50%', left: '50%', zIndex: 301,
            transform: 'translate(-50%, -50%)',
            background: 'var(--white)', borderRadius: 'var(--r)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
            padding: '28px 28px 20px', width: '360px', maxWidth: '90vw',
          }}>
            <h3 style={{ fontFamily: 'var(--serif)', fontSize: '18px', fontWeight: '400', margin: '0 0 8px' }}>
              Discard changes?
            </h3>
            <p style={{ fontSize: '14px', color: 'var(--ink-soft)', margin: '0 0 20px' }}>
              You have unsaved changes. Are you sure you want to close without saving?
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setConfirmClose(false)}
                style={cancelBtnStyle}
              >
                Keep editing
              </button>
              <button
                onClick={doClose}
                style={{
                  ...saveBtnStyle(false),
                  background: '#c03030',
                }}
              >
                Discard
              </button>
            </div>
          </div>
        </>
      )}

      <style>{`
        @keyframes slide-in-right {
          from { transform: translateX(40px); opacity: 0; }
          to   { transform: translateX(0);    opacity: 1; }
        }
        @keyframes pulse-width {
          0%, 100% { width: 30%; }
          50%       { width: 75%; }
        }
      `}</style>
    </>
  );
}

function Field({ label, required, children }) {
  return (
    <div style={{ marginBottom: '20px' }}>
      <label style={{
        display: 'block', fontSize: '12px', fontWeight: '600',
        letterSpacing: '0.05em', textTransform: 'uppercase',
        color: 'var(--ink-mute)', marginBottom: '8px',
      }}>
        {label}
        {required && <span style={{ color: '#c03030', marginLeft: '3px' }}>*</span>}
      </label>
      {children}
    </div>
  );
}

const inputStyle = {
  width: '100%', boxSizing: 'border-box',
  padding: '9px 12px', fontSize: '14px',
  fontFamily: 'var(--sans)',
  border: '1px solid var(--paper-3)', borderRadius: 'var(--r)',
  background: 'var(--white)', color: 'var(--ink)',
  outline: 'none',
};

const cancelBtnStyle = {
  padding: '8px 16px', borderRadius: 'var(--r)',
  border: '1px solid var(--paper-3)', background: 'var(--white)',
  color: 'var(--ink)', fontSize: '13px', fontFamily: 'var(--sans)',
  cursor: 'pointer',
};

const saveBtnStyle = (disabled) => ({
  padding: '8px 20px', borderRadius: 'var(--r)',
  border: 'none', background: disabled ? 'var(--paper-3)' : 'var(--ink)',
  color: disabled ? 'var(--ink-mute)' : 'var(--paper)',
  fontSize: '13px', fontFamily: 'var(--sans)',
  cursor: disabled ? 'not-allowed' : 'pointer',
  fontWeight: '500',
});
